import { SceneContextMenuBase } from "@/components/ui/scene-context-menu";
import { useViewOverlayRef } from "@/hooks/use-view-overlay-ref";
import { PanoObject } from "@/object-cache";
import {
  selectIsMeasurementBeingTaken,
  selectPanoMeasurementToolState,
} from "@/store/measurement-tool-selector";
import {
  PanoMeasurementToolState,
  PanoMeasurementWall,
  removePanoMeasurementWall,
} from "@/store/measurement-tool-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { PickingToolsRef } from "@/tools/picking-tools";
import { roundedCalculatePosition } from "@faro-lotv/app-component-toolbox";
import { DeleteIcon, FaroMenuItem } from "@faro-lotv/flat-ui";
import { Box } from "@mui/material";
import { ThreeEvent } from "@react-three/fiber";
import { useCallback, useState, type JSX } from "react";
import { Vector3 } from "three";
import { AppAwareHtml } from "../app-aware-html";
import { PanoMeasureWallRenderer } from "./pano-measure-wall-renderer";

type PanoMeasureMultiWallsRendererProps = {
  pano: PanoObject;
  walls: PanoMeasurementWall[];
} & Pick<PickingToolsRef, "onModelClicked" | "onModelHovered">;

/**
 * @returns The renderer of multiple pano measurement walls
 */
export function PanoMeasureMultiWallsRenderer({
  pano,
  walls,
  onModelHovered,
  onModelClicked,
}: PanoMeasureMultiWallsRendererProps): JSX.Element | null {
  const currentState = useAppSelector(selectPanoMeasurementToolState);
  const measureActive =
    useAppSelector(selectActiveTool) === ToolName.measurement;

  const isMeasurementBeingTaken = useAppSelector(selectIsMeasurementBeingTaken);

  const onPointerMove = useCallback(
    (ev: ThreeEvent<PointerEvent>) => {
      if (currentState === PanoMeasurementToolState.Measuring) {
        onModelHovered(ev, pano.iElement.id);
      }
    },
    [currentState, onModelHovered, pano.iElement.id],
  );

  const onClick = useCallback(
    (ev: ThreeEvent<PointerEvent>) => {
      if (currentState === PanoMeasurementToolState.Measuring) {
        onModelClicked(ev, pano.iElement.id);
      }
    },
    [currentState, onModelClicked, pano.iElement.id],
  );

  const [menuPosition, setMenuPosition] = useState<Vector3>();
  const [selectedWallId, setSelectedWallId] = useState<string>();

  const openMenu = useCallback(
    (wallId: string, point: Vector3) => {
      if (
        currentState !== PanoMeasurementToolState.None &&
        currentState !== PanoMeasurementToolState.StartMeasure &&
        !isMeasurementBeingTaken
      ) {
        setSelectedWallId(wallId);
        setMenuPosition(point);
      }
    },
    [currentState, isMeasurementBeingTaken],
  );

  const closeMenu = useCallback(() => {
    setMenuPosition(undefined);
    setSelectedWallId(undefined);
  }, []);

  if (!measureActive) return null;

  return (
    <>
      {walls.map((wall) => (
        <PanoMeasureWallRenderer
          key={wall.id}
          floorPoint1={new Vector3().fromArray(wall.floorPoint1)}
          floorPoint2={new Vector3().fromArray(wall.floorPoint2)}
          height={wall.height}
          onPointerMove={onPointerMove}
          onClick={onClick}
          onContextMenu={(ev) => openMenu(wall.id, ev.point)}
        />
      ))}
      {menuPosition && selectedWallId && (
        <PanoMeasureWallMenu
          wallId={selectedWallId}
          position={menuPosition}
          closeMenu={closeMenu}
        />
      )}
    </>
  );
}

type PanoMeasureWallProps = {
  /** The id of the selected wall */
  wallId: string;

  /** 3D position of the label menu in the scene */
  position: Vector3;

  /** Function close this menu */
  closeMenu(): void;
};

function PanoMeasureWallMenu({
  wallId,
  position,
  closeMenu,
}: PanoMeasureWallProps): JSX.Element {
  const overlayRef = useViewOverlayRef();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();

  const deleteWall = useCallback(() => {
    dispatch(removePanoMeasurementWall(wallId));
    closeMenu();
  }, [closeMenu, dispatch, wallId]);

  return (
    <AppAwareHtml
      position={position}
      portal={overlayRef}
      calculatePosition={roundedCalculatePosition}
      zIndexRange={[0, 0]}
    >
      {/* Create a empty box as anchor for the popup menu */}
      <Box component="div" ref={setAnchorEl} />
      {anchorEl && (
        <SceneContextMenuBase anchorEl={anchorEl} onClose={closeMenu}>
          <FaroMenuItem
            dark
            label="Remove wall"
            onClick={deleteWall}
            Icon={DeleteIcon}
          />
        </SceneContextMenuBase>
      )}
    </AppAwareHtml>
  );
}
